import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { HandHeart, Gift, Handshake } from 'lucide-react';
import { PageHero, PillButton } from '@/components/ui-bits';
import { Reveal } from '@/components/motion';
import { OrnamentLeaf } from '@/components/Ornaments';
import { api } from '@/lib/api';

const ICONS = [HandHeart, Gift, Handshake];
const inputCls = 'w-full rounded-2xl border border-input bg-beige/60 px-5 py-4 text-lg text-textDark placeholder:text-textDark/40 focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/25 transition-colors';

export default function Collaborate() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const kinds = [
    { v: 'collaboratore', l: t('collaborate.kindVol') },
    { v: 'sostenitore', l: t('collaborate.kindSup') },
    { v: 'partner', l: t('collaborate.kindPartner') },
  ];
  const [form, setForm] = useState({ name: '', email: '', phone: '', kind: 'collaboratore', message: '' });
  const [loading, setLoading] = useState(false);
  const ways = t('collaborate.ways', { returnObjects: true });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/collaborate', { ...form, lang });
      toast.success(t('form.success'));
      setForm({ name: '', email: '', phone: '', kind: 'collaboratore', message: '' });
    } catch {
      toast.error(t('form.error'));
    } finally {
      setLoading(false);
    }
  };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="collaborate-page">
      <PageHero
        eyebrow={t('nav.collaborate')}
        title={t('collaborate.title')}
        subtitle={t('collaborate.subtitle')}
        ornament={<OrnamentLeaf className="hidden md:block absolute right-6 top-16 w-72 text-terracotta/20" />}
      />

      <section className="px-5 md:px-10 pb-6">
        <div className="max-w-[1280px] mx-auto grid md:grid-cols-3 gap-6">
          {ways.map((w, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`collab-way-${i}`} className="h-full bg-card rounded-3xl p-8 border border-terracotta/10">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-terracotta/12 text-terracotta mb-5"><Icon size={26} /></span>
                  <h3 className="font-serif text-2xl text-textDark mb-2">{w.t}</h3>
                  <p className="text-textDark/70 text-lg">{w.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-5 md:px-10 py-16">
        <div className="max-w-[820px] mx-auto">
          <Reveal>
            <form onSubmit={submit} data-testid="collaborate-form" className="bg-rosa rounded-3xl p-8 md:p-12">
              <h2 className="font-serif text-3xl md:text-4xl text-textDark mb-8">{t('collaborate.formTitle')}</h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <input required value={form.name} onChange={upd('name')} data-testid="collab-name" className={inputCls} placeholder={t('form.name')} />
                <input required type="email" value={form.email} onChange={upd('email')} data-testid="collab-email" className={inputCls} placeholder={t('form.email')} />
              </div>
              <input value={form.phone} onChange={upd('phone')} data-testid="collab-phone" className={`${inputCls} mb-4`} placeholder={t('form.phone')} />
              <label className="block text-sm font-semibold text-textDark/70 mb-2">{t('collaborate.kind')}</label>
              <div className="flex flex-wrap gap-3 mb-4">
                {kinds.map((k) => (
                  <button type="button" key={k.v} onClick={() => setForm((f) => ({ ...f, kind: k.v }))} data-testid={`collab-kind-${k.v}`}
                    className={`rounded-full px-6 py-3 font-semibold border-2 transition-colors ${form.kind === k.v ? 'bg-terracotta text-beige border-terracotta' : 'border-textDark/20 text-textDark hover:border-terracotta'}`}>
                    {k.l}
                  </button>
                ))}
              </div>
              <textarea required rows={5} value={form.message} onChange={upd('message')} data-testid="collab-message" className={`${inputCls} mb-6 resize-none`} placeholder={t('form.message')} />
              <PillButton type="submit" disabled={loading} testId="collab-submit">{loading ? t('common.sending') : t('common.send')}</PillButton>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
